import {
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { IndividualUserInjection } from "features/individualUsersManagement/di/IndividualUserInjection";
import { IndividualUserRequestEntity } from "features/individualUsersManagement/domain/entities/IndividualUserRequestEntity";
import { IndividualUserResponseEntity } from "features/individualUsersManagement/domain/entities/IndividualUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ────────────────────────────────────────────────────────────────────

class IndividualUserInput implements Disposable {
  public fetchList = new BehaviorSubject<IndividualUserRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public resetOtp = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.resetOtp.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class IndividualUserOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<IndividualUserResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<IndividualUserResponseEntity | null>(null);
  public pagination = new BehaviorSubject<Paging>({
    page: 0,
    limit: 10,
    total: 0,
  });
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<"resetOtp" | null>(null);
  public listLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public resetOtpLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.onError.complete();
    this.actionSuccess.complete();
    this.listLoading.complete();
    this.detailLoading.complete();
    this.resetOtpLoading.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class IndividualUsersManagementViewModel extends BaseViewModel<
  IndividualUserInput,
  IndividualUserOutput
> {
  constructor() {
    super(new IndividualUserInput(), new IndividualUserOutput());
  }

  private useCase = IndividualUserInjection.getUseCase();

  protected override transform(): void {
    // Fetch user list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getIndividualUsers(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Fetch user detail
    this.skipFirstInitial(this.input.fetchDetail, async (phone) => {
      if (!phone) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getIndividualUserDetail(phone);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    // Reset OTP
    this.skipFirstInitial(this.input.resetOtp, async (phone) => {
      if (!phone) return;
      try {
        this.output.resetOtpLoading.next(true);
        const res = await this.useCase.resetOtpUser(phone);
        if (res?.data) {
          this.output.actionSuccess.next("resetOtp");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.resetOtpLoading.next(false);
      }
    });
  }
}
